import { FC, useMemo } from 'react';
import {
  Button,
  Card,
  DateRangePicker,
  Divider,
  InputSelect,
  RadixTable,
  TColumn,
} from '@features/ui';
import { DateValueType } from '@lib/utils.ts';
import { TEvalAccountByDatePaPayoutNewJodyTable, TOption } from '@src/types';
import { TReportEvalsToPaItem } from '@src/types/reports';

export const mapEvalsToPaItem = (
  item: TReportEvalsToPaItem
): TEvalAccountByDatePaPayoutNewJodyTable => ({
  packageName: item.package,
  evalAccounts: item.evals_total,
  uniqueEvalUsers: item.eval_users_total,
  qualifiedToPa: item.pa_total,
  uniquePaUser: item.pa_users_total,
  paStillActive: item.pa_active_total,
  uniqueActivePaUsers: item.pa_active_users_total,
  receivedPayouts: item.payouts_total,
  payoutTotal: Number(item.payouts_amount),
  paToPayout: Number(item.percent_pa_to_payout),
});

interface EvalAccountByDatePaPayoutNewJodyTableProps {
  data: TEvalAccountByDatePaPayoutNewJodyTable[];
  isLoading: boolean;
  selectedPackage: string;
  setSelectedPackage: (val: string) => void;
  valueStart: DateValueType;
  setValueStart: (val: DateValueType) => void;
  valueEnd: DateValueType;
  setValueEnd: (val: DateValueType) => void;
  productOptions: TOption[];
  onGet: () => void;
  onExport: () => void;
}

export const EvalAccountByDatePaPayoutNewJodyTable: FC<
  EvalAccountByDatePaPayoutNewJodyTableProps
> = ({
  data,
  isLoading,
  selectedPackage,
  setSelectedPackage,
  valueStart,
  setValueStart,
  valueEnd,
  setValueEnd,
  productOptions,
  onGet,
  onExport,
}) => {
  const columns = useMemo(
    (): TColumn<TEvalAccountByDatePaPayoutNewJodyTable>[] => [
      {
        title: 'Package Name',
        render: (item) => <p>{item?.packageName ?? '-'}</p>,
      },
      {
        title: 'Eval Accounts',
        className: 'text-center',
        render: (item) => <p>{item?.evalAccounts ?? '-'}</p>,
      },
      {
        title: 'Unique Eval Users',
        className: 'text-center',
        render: (item) => <p>{item?.uniqueEvalUsers ?? 0}</p>,
      },
      {
        title: 'Qualified to PA',
        className: 'text-center',
        render: (item) => <p>{item?.qualifiedToPa ?? 0}</p>,
      },
      {
        title: 'Unique PA Users',
        className: 'text-center',
        render: (item) => <p>{item?.uniquePaUser ?? 0}</p>,
      },
      {
        title: 'PA’s still active',
        className: 'text-center',
        render: (item) => <p>{item?.paStillActive ?? 0}</p>,
      },
      {
        title: 'Unique Active PA Users',
        className: 'text-center',
        render: (item) => <p>{item?.uniqueActivePaUsers ?? 0}</p>,
      },
      {
        title: 'Received Payouts',
        className: 'text-center',
        render: (item) => <p>{item?.receivedPayouts ?? 0}</p>,
      },
      {
        title: 'Payouts Total $',
        className: 'text-center',
        render: (item) => <p>{item?.payoutTotal ?? 0}</p>,
      },
      {
        title: 'PA (users) to Payout %',
        className: 'text-center',
        render: (item) => <p>{item?.paToPayout || '0.0'}%</p>,
      },
    ],
    []
  );

  return (
    <Card className={'mt-4.5'}>
      <div
        className={
          'flex justify-between items-start lg:flex-row flex-col mb-5 gap-6'
        }
      >
        <div className={'w-full flex gap-6 flex-wrap'}>
          <div className={'flex flex-col gap-1'}>
            <p className={'text-xs text-gray-400'}>Account Package</p>
            <InputSelect
              className={'max-w-[239px] text-white'}
              options={productOptions}
              placeholder={'All Packages'}
              value={selectedPackage}
              onChange={setSelectedPackage}
            />
          </div>
          <div className={'flex flex-col gap-1'}>
            <p className={'text-xs text-gray-400'}>From Date</p>
            <DateRangePicker
              asSingle={true}
              className={'max-w-[239px]'}
              placeholder={'From Date'}
              size={'sm'}
              value={valueStart}
              variant={'outline'}
              onChange={setValueStart}
            />
          </div>
          <div className={'flex flex-col gap-1'}>
            <p className={'text-xs text-gray-400'}>To Date</p>
            <DateRangePicker
              asSingle={true}
              className={'max-w-[239px]'}
              placeholder={'To Date'}
              size={'sm'}
              value={valueEnd}
              variant={'outline'}
              onChange={setValueEnd}
            />
          </div>
        </div>
        <div className={'flex gap-5 mt-5'}>
          <Button
            disabled={isLoading}
            size={'sm'}
            variant={'lime'}
            onClick={onGet}
          >
            Get
          </Button>
          <Button size={'sm'} variant={'secondary'} onClick={onExport}>
            Download Data
          </Button>
        </div>
      </div>
      <Divider />
      <RadixTable columns={columns} data={data} />
    </Card>
  );
};
